/** Just the `Get` call this needs, so it can be tested without a Geotab client. */
export type SortedGet<T> = (params: Record<string, unknown>, signal?: AbortSignal) => Promise<T[]>;

export interface FetchAllSortedByDateOptions {
  typeName: string;
  search: object;
  /** `resultsLimit` per request; a page this full means there may be more. */
  pageSize: number;
  /** Upper bound on requests, so one selection cannot download without end. */
  maxPages: number;
  signal?: AbortSignal;
}

export interface SortedByDateResult<T> {
  items: T[];
  /** False when `maxPages` full pages came back, so rows past the last one may be missing. */
  complete: boolean;
}

/**
 * Every row of a dated entity, paged with `Get`'s `sort` (date ascending, continuing after the
 * last row's `dateTime` + `id`) until a page comes back short or `maxPages` is reached.
 *
 * The Get method reference lists no `sort` parameter; its code sample sends
 * `sort: { sortBy, sortDirection, offset, lastId }`. Paging FaultData this way was verified in the
 * API Runner (2026-10-14): ordered, no overlap between pages.
 */
export async function fetchAllSortedByDate<T extends { id?: string; dateTime?: Date | string }>(
  get: SortedGet<T>,
  options: FetchAllSortedByDateOptions,
): Promise<SortedByDateResult<T>> {
  const { typeName, search, pageSize, maxPages, signal } = options;
  const items: T[] = [];
  let cursor: { offset: Date | string; lastId: string } | undefined;

  for (let page = 0; page < maxPages; page++) {
    signal?.throwIfAborted();

    const rows = await get(
      {
        typeName,
        search,
        resultsLimit: pageSize,
        sort: { sortBy: "date", sortDirection: "asc", ...cursor },
      },
      signal,
    );

    if (!Array.isArray(rows)) {
      throw new Error(`Unexpected ${typeName} response: expected an array, got ${String(rows)}`);
    }

    // Pushed one at a time rather than spread: a full page is more arguments than a call can take.
    for (const row of rows) {
      items.push(row);
    }

    if (rows.length < pageSize) {
      return { items, complete: true };
    }

    const last = rows[rows.length - 1];
    if (!last?.id || !last.dateTime) {
      throw new Error(`Cannot page ${typeName}: the last row of a full page has no id or dateTime`);
    }
    cursor = { offset: last.dateTime, lastId: last.id };
  }

  return { items, complete: false };
}
